import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppHeader from "@/components/app/AppHeader";
import AppSection from "@/components/app/AppSection";
import { EmptyState } from "@/components/app/EmptyState";
import { StatusBadge } from "@/components/app/StatusBadge";
import CTAButton from "@/components/app/CTAButton";
import { api, formatApiError } from "@/lib/api";
import { Compass, Plus, ArrowRight } from "lucide-react";

const statusTone = {
    draft: "neutral",
    preview: "gold",
    active: "primary",
    completed: "success",
    archived: "neutral",
};
const statusLabel = {
    draft: "Brouillon",
    preview: "Prête à lancer",
    active: "En cours",
    completed: "Terminée",
    archived: "Archivée",
};

export default function AppMissions() {
    const navigate = useNavigate();
    const [missions, setMissions] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/missions")
            .then(({ data }) => setMissions(data))
            .catch((e) => { setMissions([]); setError(formatApiError(e)); });
    }, []);

    const active = (missions || []).find((m) => ["draft", "preview", "active"].includes(m.status));
    const past = (missions || []).filter((m) => !["draft", "preview", "active"].includes(m.status));

    return (
        <>
            <AppHeader
                title="Mes missions"
                subtitle="Une mission à la fois — pour aller au bout."
            />

            {error && (
                <AppSection>
                    <div className="rounded-lg border border-md-danger/30 bg-md-danger/10 px-3 py-2 text-xs text-md-danger">{error}</div>
                </AppSection>
            )}

            {missions === null ? (
                <AppSection><div className="py-16 text-center text-sm text-md-text-2">Chargement…</div></AppSection>
            ) : !active && past.length === 0 ? (
                <AppSection>
                    <EmptyState
                        icon={<Compass className="h-6 w-6" />}
                        title="Aucune mission encore"
                        description="Une mission est ton voyage de complétion. Une seule à la fois, mais menée jusqu’au bout."
                        action={
                            <Link to="/app/missions/new" data-testid="missions-empty-cta-new">
                                <CTAButton icon={<Plus className="h-4 w-4" />}>Préparer ma première mission</CTAButton>
                            </Link>
                        }
                    />
                </AppSection>
            ) : (
                <>
                    {active && (
                        <AppSection>
                            <p className="md-overline mb-3">EN COURS</p>
                            <button
                                onClick={() => navigate(`/app/missions/${active._id}`)}
                                data-testid="missions-active-card"
                                className="group w-full rounded-2xl border border-md-secondary/30 bg-md-surface p-5 text-left transition hover:border-md-secondary"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="truncate text-base font-bold tracking-tight text-md-text">{active.title || "Mission sans titre"}</p>
                                        <p className="mt-1 text-xs text-md-text-2">
                                            {active.item_count} carte{active.item_count > 1 ? "s" : ""} · estimation {(active.total_eur || 0).toFixed(2).replace(".", ",")} €
                                        </p>
                                    </div>
                                    <StatusBadge tone={statusTone[active.status]}>{statusLabel[active.status]}</StatusBadge>
                                </div>
                                <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-md-secondary">
                                    Ouvrir <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                                </div>
                            </button>
                        </AppSection>
                    )}

                    {!active && (
                        <AppSection>
                            <Link to="/app/missions/new" data-testid="missions-new-cta">
                                <CTAButton size="block" icon={<Plus className="h-4 w-4" />}>Préparer une nouvelle mission</CTAButton>
                            </Link>
                        </AppSection>
                    )}

                    {past.length > 0 && (
                        <AppSection>
                            <p className="md-overline mb-3">HISTORIQUE</p>
                            <ul className="space-y-2">
                                {past.map((m) => (
                                    <li
                                        key={m._id}
                                        onClick={() => navigate(`/app/missions/${m._id}`)}
                                        data-testid={`missions-past-${m._id}`}
                                        className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-md-border bg-md-surface p-4 hover:bg-md-surface-2"
                                    >
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold text-md-text">{m.title}</p>
                                            <p className="mt-0.5 text-[11px] text-md-text-2">{m.item_count} cartes · {(m.total_eur || 0).toFixed(2).replace(".", ",")} €</p>
                                        </div>
                                        <StatusBadge tone={statusTone[m.status]}>{statusLabel[m.status]}</StatusBadge>
                                    </li>
                                ))}
                            </ul>
                        </AppSection>
                    )}
                </>
            )}
        </>
    );
}
